import type { DeepReadonly, Ref, ToRefs } from 'vue'
import { FetchError } from 'ofetch'
import type { Deck, IncludeCompany, Slide } from '@/entities/deck'
import type { User } from '@/entities/user'
import type { SelectedHotspot, SiteChangeEvent, MeetingHotspotName } from '@/entities/site'
import { sha512 } from 'js-sha512'
import { findLastIndex, pipe, slice } from 'ramda'
import { MeetingHotspotNameMap } from '@/entities/site'
import type { DeckEmbed } from '@/entities/embed'

const OPENABLE_HOTSPOTS = ['P', 'I', 'V', 'N', 'L', '3']
export const PASSWORD_REQUIRED_HEADER_MSG = 'SCRAM-SHA-512 realm="Access to private deck with password"'

export class UseDeckAuthError extends FetchError {
  constructor(message: string) {
    super(message)
    this.name = 'UseDeckAuthError'
  }
}

export class UseDeckPasswordRequiredError extends FetchError {
  constructor(message: string) {
    super(message)
    this.name = 'PasswordRequiredError'
  }
}

export class UseDeckPermissionsError extends FetchError {
  constructor(message: string) {
    super(message)
    this.name = 'PermissionsError'
  }
}

export class UseDeckLinkExpiredError extends FetchError {
  constructor(message: string) {
    super(message)
    this.name = 'LinkExpiredError'
  }
}

type UseDeckError = UseDeckAuthError | UseDeckPasswordRequiredError | UseDeckPermissionsError | UseDeckLinkExpiredError | Error

export interface SiteInitialState {
  coordinate: Point | null
  zoom: number | null
}

export interface SiteState {
  siteId: number | null
  cameraId: number | null
  selectedHotspot: SelectedHotspot | null
  siteHistory: SiteChangeEvent[]
  initialState: SiteInitialState
}

interface State {
  initialized: boolean
  loading: boolean
  error: UseDeckError | null
  deck: (Deck & IncludeCompany) | null
  currentSlideIndex: number
  slideRefreshKey: number
  site: SiteState
}

interface NewSiteHistory {
  site_history: SiteChangeEvent[]
  location?: string | null
  location_id?: number | null
}

export interface UseDeck extends ToRefs<DeepReadonly<State>> {
  firstValidSlide: DeepReadonly<Ref<number>>
  lastValidSlide: DeepReadonly<Ref<number>>
  onStartAutoPlay(): void
  setCurrentSlideIndex(index: number): void
  previousSlide(): void
  nextSlide(): void
  isSelectableSlide(slide: Slide | DeepReadonly<Slide>): boolean
  onCycleNextSlide(): void
  submitPassword(params: { password: string }): Promise<void>
  refreshDeck(options?: { signal?: AbortSignal }): Promise<void>
  refreshSlides(): Promise<void>
  updateSlides(slides: Slide[]): void
  setSiteId(siteId: number | null): void
  setSelectedHotspot(hotspot: SelectedHotspot | null): void
  setSiteHistory(newSiteHistory: NewSiteHistory): void
  setSiteInitialState(initialState: SiteInitialState): void
  getEmbeds(): Promise<DeckEmbed[]>
  refetchHighResImages(): Promise<void>
  updateSlideRefreshKey(): void
}

const initialState: State = {
  initialized: true,
  loading: false,
  error: null,
  deck: null,
  currentSlideIndex: 0,
  slideRefreshKey: 0,
  site: reactive({
    siteId: null,
    cameraId: null,
    selectedHotspot: null,
    siteHistory: [],
    initialState: {
      coordinate: null,
      zoom: null,
    },
  }),
}

const COOKIE_EXPIRATION = 60 * 60 * 24 * 14 // 14 days

export function useDeck({
  id,
  isInMeeting,
  user,
  initialSlideId,
  onCurrentSlideChange,
  onDeckLoaded,
  onHotspotChange,
  include = ['company', 'site_image'],
  includes,
  requiresEdit,
}: {
  id: Ref<string>
  isInMeeting?: boolean
  user: Ref<DeepReadonly<User | undefined>>
  initialSlideId?: Ref<number | null>
  onCurrentSlideChange?: (slide: Slide) => void
  onDeckLoaded?: () => void
  onHotspotChange?: (hotspot: SelectedHotspot | null) => void
  include?: string[]
  /** Sparse field includes (e.g. press_release_link) — separate from legacy `include`. */
  includes?: string[]
  requiresEdit?: boolean
}): UseDeck {
  const globalFetchHeaders = useState<{ [key: string]: string }>('global-fetch-headers', () => ({ 'X-Application': 'web' }))
  const route = useRoute()
  const authIdCookie = useCookie(`auth${id.value}`, { maxAge: COOKIE_EXPIRATION })
  const state = reactive<State>({ ...initialState })
  const firstValidSlide = computed<number>(() => state.deck?.slides.findIndex(isSelectableSlide) ?? 0)
  const lastValidSlide = computed<number>(() => findLastIndex(isSelectableSlide, state.deck?.slides ?? []))
  const hotspotHistory: SelectedHotspot[] = []

  const autoPlaySlideIndices = computed<number[]>(
    () =>
      state.deck?.slides
        .map((s: Slide, i) => {
          if (!['E', 'H'].includes(s.type)) return i
        })
        .filter(Number.isInteger) as number[],
  )

  function onCycleNextSlide() {
    const nextSlideIndex = autoPlaySlideIndices.value.find(i => i && i > state.currentSlideIndex)

    state.currentSlideIndex = nextSlideIndex ?? autoPlaySlideIndices.value[0]!
    _onCurrentSlideChange()
  }

  function onStartAutoPlay() {
    if (!autoPlaySlideIndices.value.length) return

    if (!autoPlaySlideIndices.value.includes(state.currentSlideIndex)) {
      setCurrentSlideIndex(autoPlaySlideIndices.value.find(i => i && i > state.currentSlideIndex) ?? autoPlaySlideIndices.value[0]!)
    }
  }

  function setCurrentSlideIndex(index: number): void {
    state.currentSlideIndex = index

    _onCurrentSlideChange()
  }

  function previousSlide() {
    const newIndex = pipe<[readonly Slide[]], Slide[], number>(
      slice(0, state.currentSlideIndex),
      findLastIndex(isSelectableSlide),
    )(state.deck?.slides ?? [])

    if (newIndex > -1) {
      state.currentSlideIndex = newIndex
      _onCurrentSlideChange()
    } else {
      state.currentSlideIndex = firstValidSlide.value
    }
  }

  function nextSlide() {
    const slideIncrement = state.deck?.slides.slice(state.currentSlideIndex + 1).findIndex(isSelectableSlide)

    if (slideIncrement !== undefined && slideIncrement > -1) {
      state.currentSlideIndex = state.currentSlideIndex + slideIncrement + 1
      _onCurrentSlideChange()
    } else {
      state.currentSlideIndex = lastValidSlide.value
    }
  }

  async function submitPassword({ password }: { password: string }): Promise<void> {
    try {
      state.initialized = false
      state.loading = true
      state.error = null

      const hashedPassword = sha512(password)
      const res = await $fetch<Deck & IncludeCompany>(`v2/decks/${id.value}`, {
        ...useFetchSharedOptions(),
        params: {
          include,
          inactive_meeting_slides: false,
          ...(includes?.length ? { includes } : {}),
        },
        headers: {
          ...globalFetchHeaders.value,
          'x-deck-password': hashedPassword ? hashedPassword : 'null',
          'X-Deck-Auth-UUID': route.query?.auth ? route.query?.auth.toString() : 'null',
        },
      })

      _setNewDeckState(res)

      state.initialized = true
      state.error = null
    } catch (e) {
      if (e instanceof Error) {
        const error = new UseDeckPasswordRequiredError(e.message)
        state.error = error
        throw error
      }

      throw e
    } finally {
      state.loading = false
    }

    authIdCookie.value = sha512(password)
  }

  function setSiteId(siteId: number | null) {
    state.site.siteId = siteId
    state.site.cameraId = null

    if (hotspotHistory.length === 0 || (hotspotHistory.length === 1 && hotspotHistory[0].type !== 'L')) {
      state.site.cameraId = state.deck?.slides[state.currentSlideIndex].camera_id ?? null
    }

    if (state.site.selectedHotspot && siteId && state.site.selectedHotspot.siteId !== siteId) {
      state.site.selectedHotspot.siteId = siteId
    }
  }

  function setSelectedHotspot(hotspot: SelectedHotspot | null) {
    if (hotspot?.type && !OPENABLE_HOTSPOTS.includes(hotspot.type)) return

    if (hotspot?.type === state.site.selectedHotspot?.type && hotspot?.dataId === state.site.selectedHotspot?.dataId) {
      // sometimes happens on multiple state:current calls
      return
    }

    if (hotspot?.type === 'L') {
      state.site.siteId = hotspot.dataId
      state.site.cameraId = null
      state.site.initialState = { coordinate: null, zoom: null }
    }

    if (hotspot) {
      if (!hotspot.siteId && state.site.siteId) {
        // siteId of current hotspot should be dataId of the previous hotspot.
        // this is added for info hotspots (type=N) which are not really hotspots at all.
        hotspot.siteId = state.site.siteId
      }
      _pushSiteHistory(hotspot)
      state.site.selectedHotspot = hotspot
    } else {
      _onSiteBack()
      state.site.selectedHotspot = hotspotHistory[hotspotHistory.length - 1] ?? null
    }

    onHotspotChange?.(hotspot)
  }

  function setSiteHistory(newSiteHistory: NewSiteHistory) {
    state.site.siteHistory = newSiteHistory.site_history

    hotspotHistory.splice(
      0,
      hotspotHistory.length,
      ...newSiteHistory.site_history.map((site, ix, siteHistory) => {
        if (ix === siteHistory.length - 1) {
          return {
            type: MeetingHotspotNameMap[newSiteHistory.location as MeetingHotspotName],
            siteId: site.from_site_id,
            dataId: newSiteHistory.location_id as number,
          }
        }
        return {
          type: 'L' as const,
          siteId: site.from_site_id,
          dataId: siteHistory[ix + 1].from_site_id as number,
        }
      }),
    )

    // hack to accommodate info hotspots, which the socket doesn't treat as actual hotspots
    // this fixes a race condition when the info hotspot was set before the site history stack.
    const { selectedHotspot } = state.site
    if (selectedHotspot?.type === 'N') {
      newSiteHistory.site_history.push({
        from_site_id: selectedHotspot.siteId,
        look_at: selectedHotspot.lookAt,
        zoom: selectedHotspot.zoom,
      })
      hotspotHistory.push(selectedHotspot)
    }

    if (hotspotHistory[hotspotHistory.length - 1]) {
      state.site.selectedHotspot = hotspotHistory[hotspotHistory.length - 1]
    }
  }

  function setSiteInitialState(payload: SiteInitialState) {
    state.site.initialState = payload
  }

  const _fetchDeckInner = async (id: string, options?: { signal?: AbortSignal }): Promise<(Deck & IncludeCompany) | null> => {
    const oldSignal = options?.signal

    try {
      state.initialized = false
      state.loading = true
      state.error = null

      const res = await $fetch<Deck & IncludeCompany>(`v2/decks/${id}`, {
        ...useFetchSharedOptions(),
        params: {
          include,
          inactive_meeting_slides: include.includes('inactive_meeting_slides'),
          ...(includes?.length ? { includes } : {}),
        },
        headers: {
          ...globalFetchHeaders.value,
          'x-deck-password': authIdCookie.value ? authIdCookie.value : 'null',
          'X-Deck-Auth-UUID': route.query?.auth ? route.query?.auth.toString() : 'null',
        },
        ...options,
      })

      state.initialized = true

      // Check permissions if edit access is required
      if (requiresEdit && !res.permissions?.update) {
        state.error = new UseDeckPermissionsError('Insufficient permissions for edit access')
        return null
      }

      return res
    } catch (e) {
      if (oldSignal?.aborted) {
        return null
      }

      if (e instanceof FetchError) {
        if (e.response?.status === 401 && e.response?.headers.get('www-authenticate') === PASSWORD_REQUIRED_HEADER_MSG) {
          state.error = new UseDeckPasswordRequiredError(e.message)
        } else if (e.response?.status === 410) {
          state.error = new UseDeckLinkExpiredError(e.message)
        } else if (e.response?.status === 404 || e.response?.status === 403) {
          state.error = new UseDeckPermissionsError(e.message)
        } else if (!user.value) {
          state.error = new UseDeckAuthError(e.message)
        } else {
          state.error = e
        }
      }
      return null
    } finally {
      state.loading = false
    }
  }

  async function fetchDeck(id: string, options?: { signal?: AbortSignal }): Promise<void> {
    const deck = await _fetchDeckInner(id, options)
    if (deck) _setNewDeckState(deck)
  }

  async function refreshSlides(): Promise<void> {
    const deck = await _fetchDeckInner(id.value)
    if (deck && state.deck) {
      state.deck = { ...state.deck, slides: deck.slides.map(_disableNonMeetingSlides) }
      state.slideRefreshKey = Date.now()
    }
  }

  function updateSlides(slides: Slide[]): void {
    if (state.deck) {
      state.deck = { ...state.deck, slides: slides.map(_disableNonMeetingSlides) }
      state.slideRefreshKey = Date.now()
    }
  }

  async function refetchHighResImages(): Promise<void> {
    const deck = await _fetchDeckInner(id.value)

    if (deck && state.deck) {
      const imageAndPdfSlideTypes = ['I', 'P']

      state.deck = {
        ...state.deck,
        slides: [
          ...state.deck.slides.filter(s => !imageAndPdfSlideTypes.includes(s.type)),
          ...deck.slides.filter(s => imageAndPdfSlideTypes.includes(s.type)),
        ],
      }
    }
  }

  // Lifecycle

  watch(
    id,
    async id => {
      if (!id) return

      // Reset state
      Object.assign(state, initialState)

      await fetchDeck(id)

      onDeckLoaded?.()
    },
    { immediate: true },
  )

  // Private

  function _onSiteBack(): SiteChangeEvent | undefined {
    const poppedVal = state.site.siteHistory[state.site.siteHistory.length - 1] ?? {}

    const fromSiteId = poppedVal.from_site_id
    if (fromSiteId) {
      state.site.siteId = fromSiteId
      if (fromSiteId === state.deck?.slides[state.currentSlideIndex].site_id) {
        state.site.cameraId = state.deck?.slides[state.currentSlideIndex].camera_id ?? null
      }
    }

    state.site.initialState = {
      ...state.site.initialState,
      coordinate: poppedVal.look_at ?? null,
      zoom: poppedVal.zoom ?? null,
    }

    state.site.siteHistory = state.site.siteHistory.slice(0, state.site.siteHistory.length - 1)
    hotspotHistory.pop()

    return poppedVal
  }

  // Update siteId for site slide
  function _resetSite() {
    if (state.deck?.slides[state.currentSlideIndex]?.type === 'S') {
      state.site.siteId = state.deck.slides[state.currentSlideIndex].site_id
      state.site.cameraId = state.deck.slides[state.currentSlideIndex].camera_id
    } else {
      state.site.siteId = null
      state.site.cameraId = null
    }

    state.site.selectedHotspot = null
    state.site.siteHistory = []
    hotspotHistory.splice(0) // parallel state object
    state.site.initialState = { coordinate: null, zoom: null }
  }

  function isSelectableSlide(slide: Slide): boolean {
    return slide.type !== 'H' && slide.type !== 'T' && !slide.disabled
  }

  const getEmbeds = async () => {
    if (!state.deck) return []
    const companyId = state.deck.company_id
    const deckId = id.value
    const res = await $fetch<{ results: DeckEmbed[] }>(`v2/companies/${companyId}/embed/decks`, {
      ...useFetchSharedOptions(),
      params: { limit: 1000, deck_id: deckId },
      headers: {
        ...globalFetchHeaders.value,
        'X-Deck-Id': id.value,
      },
    })
    return res.results
  }

  let isInitialSlideChange = true
  function _onCurrentSlideChange(): void {
    if (isInitialSlideChange) {
      isInitialSlideChange = false
      return
    }
    if (state.initialized) _resetSite()
    state.slideRefreshKey = Date.now()
    if (!onCurrentSlideChange) return
    const slide = state.deck?.slides[state.currentSlideIndex]
    if (slide && !state.loading) onCurrentSlideChange(slide)
  }

  function _setNewDeckState(deck: Deck & IncludeCompany) {
    state.deck = {
      ...deck,
      // Filter out BAM slides (slide.type=E)
      slides: deck.slides.filter(s => s.type !== 'E').map(_disableNonMeetingSlides),
    }

    if (initialSlideId?.value) {
      const index = state.deck?.slides?.findIndex(({ id }) => id === initialSlideId.value)
      if (index !== -1) {
        setCurrentSlideIndex(index)
      } else {
        setCurrentSlideIndex(firstValidSlide.value)
      }
    } else {
      setCurrentSlideIndex(firstValidSlide.value)
    }

    _resetSite()
  }

  function _pushSiteHistory(hotspot: SelectedHotspot) {
    state.site.siteHistory.push({
      from_site_id: hotspot.siteId,
      look_at: hotspot.lookAt,
      zoom: hotspot.zoom,
    })
    hotspotHistory.push(hotspot)
  }

  function _disableNonMeetingSlides(slide: Slide): Slide {
    if (!isInMeeting) return slide

    return { ...slide, disabled: slide.type === 'E' || slide.type === 'V' }
  }

  return {
    ...toRefs(readonly(state)),
    onStartAutoPlay,
    setCurrentSlideIndex,
    firstValidSlide,
    lastValidSlide,
    previousSlide,
    nextSlide,
    isSelectableSlide,
    onCycleNextSlide,
    submitPassword,
    setSiteId,
    setSelectedHotspot,
    setSiteHistory,
    setSiteInitialState,
    getEmbeds,
    refreshDeck: (options?: { signal?: AbortSignal }) => fetchDeck(id.value, options),
    refreshSlides,
    updateSlides,
    refetchHighResImages,
    updateSlideRefreshKey: () => (state.slideRefreshKey = Date.now()),
  }
}

export function generateDeckTitle(deck: EditDeck | null) {
  return !deck ? 'Untitled' : `${deck.title} - ${deck.company.name}`
}
